import { downloadArtifact, getArtifact, updateArtifactContent, type CoworkArtifact } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { useParams, useSearchParams } from "@solidjs/router"
import { createMemo, For, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"

type XlsxCell = { v?: unknown; w?: string; f?: string; t?: string; s?: unknown }
type XlsxDimension = { wpx?: number; wch?: number; hpx?: number; hpt?: number }
type XlsxSheet = Record<string, unknown> & {
  "!ref"?: string
  "!cols"?: XlsxDimension[]
  "!rows"?: XlsxDimension[]
  "!merges"?: XlsxRange[]
  "!autofilter"?: { ref: string }
}
type XlsxWorkbook = { SheetNames: string[]; Sheets: Record<string, XlsxSheet>; vbaraw?: unknown }
type XlsxRange = { s: { r: number; c: number }; e: { r: number; c: number } }
type HeaderMenu = { axis: "row" | "column"; index: number; x: number; y: number }
type XlsxModule = {
  read(data: ArrayBuffer, options: Record<string, unknown>): XlsxWorkbook
  write(workbook: XlsxWorkbook, options: Record<string, unknown>): ArrayBuffer
  utils: {
    decode_range(reference: string): XlsxRange
    decode_cell(address: string): { r: number; c: number }
    encode_cell(cell: { r: number; c: number }): string
    encode_range(range: XlsxRange): string
  }
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
const XLSM_MIME = "application/vnd.ms-excel.sheet.macroEnabled.12"
const DEFAULT_COLUMN_WIDTH = 140
const DEFAULT_ROW_HEIGHT = 32

export function CoworkReportEditorPage() {
  const language = useLanguage()
  const params = useParams<{ projectID: string; artifactID: string }>()
  const [search] = useSearchParams<{ companyID?: string }>()
  const [state, setState] = createStore({
    report: undefined as CoworkArtifact | undefined,
    sheet: "",
    rows: 0,
    columns: 0,
    revision: 0,
    loading: true,
    saving: false,
    dirty: false,
    error: "",
    columnWidths: {} as Record<number, number>,
    rowHeights: {} as Record<number, number>,
    menu: undefined as HeaderMenu | undefined,
  })
  let XLSX: XlsxModule | undefined
  let workbook: XlsxWorkbook | undefined
  let stopResize: (() => void) | undefined

  const sheet = () => workbook?.Sheets[state.sheet]
  const rowIndexes = createMemo(() => {
    state.revision
    return Array.from({ length: state.rows }, (_, index) => index)
  })
  const columnIndexes = createMemo(() => {
    state.revision
    return Array.from({ length: state.columns }, (_, index) => index)
  })

  const selectSheet = (name: string) => {
    if (!XLSX || !workbook?.Sheets[name]) return
    const range = workbookRange(XLSX, workbook.Sheets[name])
    const rows = range.e.r + 3
    const columns = range.e.c + 3
    setState({
      sheet: name,
      rows,
      columns,
      rowHeights: dimensionSizes(workbook.Sheets[name]["!rows"], rows, "row"),
      columnWidths: dimensionSizes(workbook.Sheets[name]["!cols"], columns, "column"),
      menu: undefined,
    })
  }

  const load = async () => {
    setState({ loading: true, error: "" })
    try {
      const companyID = search.companyID
      const [report, download, module] = await Promise.all([
        getArtifact(params.projectID, params.artifactID, companyID),
        downloadArtifact(params.projectID, params.artifactID, companyID),
        loadXlsxModule(),
      ])
      XLSX = module
      workbook = module.read(await download.blob.arrayBuffer(), {
        type: "array",
        cellDates: true,
        cellFormula: true,
        cellStyles: true,
        bookVBA: true,
      })
      setState({ report, dirty: false, revision: 0 })
      selectSheet(workbook.SheetNames[0] ?? "")
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("loading", false)
    }
  }

  const updateCell = (row: number, column: number, value: string) => {
    if (!XLSX || !sheet()) return
    const address = XLSX.utils.encode_cell({ r: row, c: column })
    const previous = xlsxCell(sheet()![address])
    const style = previous?.s
    sheet()![address] = parseCell(value, style)
    const range = workbookRange(XLSX, sheet()!)
    range.e.r = Math.max(range.e.r, row)
    range.e.c = Math.max(range.e.c, column)
    sheet()!["!ref"] = XLSX.utils.encode_range(range)
    setState({ dirty: true, revision: state.revision + 1 })
  }

  const resizeDimension = (axis: "row" | "column", index: number, event: PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    stopResize?.()
    const start = axis === "column" ? event.clientX : event.clientY
    const initial = axis === "column" ? columnWidth(state.columnWidths, index) : rowHeight(state.rowHeights, index)
    const move = (next: PointerEvent) => {
      const offset = (axis === "column" ? next.clientX : next.clientY) - start
      const size = Math.max(axis === "column" ? 56 : 22, initial + offset)
      if (axis === "column") setState("columnWidths", index, size)
      else setState("rowHeights", index, size)
    }
    const finish = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", finish)
      stopResize = undefined
      const current = sheet()
      if (!current) return
      if (axis === "column") {
        const dimensions = [...(current["!cols"] ?? [])]
        dimensions[index] = { ...(dimensions[index] ?? {}), wpx: columnWidth(state.columnWidths, index) }
        current["!cols"] = dimensions
      } else {
        const dimensions = [...(current["!rows"] ?? [])]
        dimensions[index] = { ...(dimensions[index] ?? {}), hpx: rowHeight(state.rowHeights, index) }
        current["!rows"] = dimensions
      }
      setState({ dirty: true, revision: state.revision + 1 })
    }
    stopResize = finish
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", finish, { once: true })
  }

  const changeStructure = (axis: "row" | "column", index: number, offset: 1 | -1) => {
    if (!XLSX || !workbook || !sheet()) return
    if (offset < 0 && (axis === "row" ? state.rows : state.columns) <= 1) return
    shiftWorksheet(XLSX, workbook, state.sheet, axis, index, offset)
    if (axis === "row") {
      setState({
        rows: state.rows + offset,
        rowHeights: shiftDimensionSizes(state.rowHeights, index, offset, DEFAULT_ROW_HEIGHT),
      })
    } else {
      setState({
        columns: state.columns + offset,
        columnWidths: shiftDimensionSizes(state.columnWidths, index, offset, DEFAULT_COLUMN_WIDTH),
      })
    }
    setState({ menu: undefined, dirty: true, revision: state.revision + 1 })
  }

  const openHeaderMenu = (axis: "row" | "column", index: number, event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setState("menu", {
      axis,
      index,
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - 144)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - 88)),
    })
  }

  const save = async () => {
    if (!XLSX || !workbook || !state.report || state.saving || !state.dirty) return
    setState({ saving: true, error: "" })
    try {
      const macroEnabled = normalizedFormat(state.report.format) === "xlsm"
      const content = XLSX.write(workbook, {
        type: "array",
        bookType: macroEnabled ? "xlsm" : "xlsx",
        bookVBA: macroEnabled,
        cellStyles: true,
      })
      const report = await updateArtifactContent(
        params.projectID,
        params.artifactID,
        new Blob([content], { type: macroEnabled ? XLSM_MIME : XLSX_MIME }),
        state.report.updated_at,
        search.companyID,
      )
      setState({ report, dirty: false })
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("saving", false)
    }
  }

  const beforeUnload = (event: BeforeUnloadEvent) => {
    if (!state.dirty) return
    event.preventDefault()
    event.returnValue = ""
  }
  onMount(() => {
    window.addEventListener("beforeunload", beforeUnload)
    window.addEventListener("click", closeHeaderMenu)
    void load()
  })
  const closeHeaderMenu = () => setState("menu", undefined)
  onCleanup(() => {
    stopResize?.()
    window.removeEventListener("beforeunload", beforeUnload)
    window.removeEventListener("click", closeHeaderMenu)
  })

  return (
    <main class="flex h-full min-h-0 flex-col bg-v2-background-bg-deep">
      <header class="flex shrink-0 items-center justify-between gap-4 border-b border-v2-border-border-base bg-v2-background-bg-base px-4 py-3">
        <div class="min-w-0">
          <h1 class="truncate text-[15px] font-semibold">{state.report?.title ?? language.t("common.loading")}</h1>
          <p class="mt-0.5 text-[10px] uppercase text-v2-text-text-muted">{state.report?.format ?? "xlsx"}</p>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex h-8 items-center gap-2 rounded-md bg-v2-button-solid-bg-base px-3 text-12-medium text-v2-button-solid-fg-base disabled:opacity-50"
            disabled={state.loading || state.saving || !state.dirty}
            onClick={() => void save()}
          >
            <IconV2 name="check" size="small" />
            {state.saving ? language.t("common.saving") : language.t("common.save")}
          </button>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-2 rounded-md border border-v2-border-border-base px-3 text-12-medium hover:bg-v2-background-bg-layer-02"
            onClick={() => window.close()}
          >
            <IconV2 name="close" size="small" />
            {language.t("common.close")}
          </button>
        </div>
      </header>

      <Show when={state.error}>
        <div class="shrink-0 bg-v2-state-bg-danger px-4 py-3 text-12-regular text-v2-state-fg-danger">
          {state.error}
        </div>
      </Show>
      <Show
        when={!state.loading && workbook && state.sheet}
        fallback={
          <Show when={!state.error}>
            <div class="grid flex-1 place-items-center text-13-regular text-v2-text-text-muted">
              {language.t("common.loading")}
            </div>
          </Show>
        }
      >
        <div
          class="min-h-0 flex-1 overflow-auto bg-white [scrollbar-color:#888_transparent] [scrollbar-width:thin]"
          style={{ "background-color": "#ffffff", color: "#1f1f1f", "color-scheme": "light" }}
        >
          <table class="border-separate border-spacing-0 text-left text-12-regular text-[#1f1f1f]">
            <colgroup>
              <col style={{ width: "48px" }} />
              <For each={columnIndexes()}>
                {(column) => <col style={{ width: `${columnWidth(state.columnWidths, column)}px` }} />}
              </For>
            </colgroup>
            <thead>
              <tr>
                <th class="sticky left-0 top-0 z-30 h-7 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3]" />
                <For each={columnIndexes()}>
                  {(column) => (
                    <th
                      class="group relative sticky top-0 z-20 h-7 border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-center text-11-medium text-[#555]"
                      style={{ width: `${columnWidth(state.columnWidths, column)}px` }}
                      onContextMenu={(event) => openHeaderMenu("column", column, event)}
                    >
                      {spreadsheetColumnName(column)}
                      <button
                        type="button"
                        class="absolute right-1 top-1/2 hidden -translate-y-1/2 rounded p-0.5 hover:bg-[#dedede] group-hover:block"
                        title={language.t("cowork.enterprise.knowledge.add")}
                        aria-label={language.t("cowork.enterprise.knowledge.add")}
                        onClick={(event) => openHeaderMenu("column", column, event)}
                      >
                        <IconV2 name="chevron-down" size="small" />
                      </button>
                      <span
                        class="absolute -right-1 top-0 h-full w-2 cursor-col-resize touch-none"
                        onPointerDown={(event) => resizeDimension("column", column, event)}
                      />
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <For each={rowIndexes()}>
                {(row) => (
                  <tr style={{ height: `${rowHeight(state.rowHeights, row)}px` }}>
                    <th
                      class="group relative sticky left-0 z-10 min-w-12 border-b border-r border-[#d0d0d0] bg-[#f3f3f3] px-2 text-right text-11-medium text-[#555]"
                      onContextMenu={(event) => openHeaderMenu("row", row, event)}
                    >
                      {row + 1}
                      <button
                        type="button"
                        class="absolute left-1 top-1/2 hidden -translate-y-1/2 rounded p-0.5 hover:bg-[#dedede] group-hover:block"
                        title={language.t("cowork.enterprise.knowledge.add")}
                        aria-label={language.t("cowork.enterprise.knowledge.add")}
                        onClick={(event) => openHeaderMenu("row", row, event)}
                      >
                        <IconV2 name="chevron-down" size="small" />
                      </button>
                      <span
                        class="absolute -bottom-1 left-0 h-2 w-full cursor-row-resize touch-none"
                        onPointerDown={(event) => resizeDimension("row", row, event)}
                      />
                    </th>
                    <For each={columnIndexes()}>
                      {(column) => (
                        <td class="border-b border-r border-[#d9d9d9] bg-white p-0">
                          <input
                            class="h-full w-full bg-transparent px-2 py-1 text-[#1f1f1f] outline-none focus:ring-2 focus:ring-inset focus:ring-[#217346]"
                            style={{ "background-color": "#ffffff", color: "#1f1f1f", "caret-color": "#1f1f1f" }}
                            aria-label={`${spreadsheetColumnName(column)}${row + 1}`}
                            spellcheck={false}
                            value={cellText(XLSX!, sheet()!, row, column, state.revision)}
                            onChange={(event) => updateCell(row, column, event.currentTarget.value)}
                          />
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <Show when={state.menu} keyed>
          {(menu) => (
            <div
              class="fixed z-50 min-w-32 rounded-md border border-v2-border-border-base bg-v2-background-bg-base p-1 shadow-lg"
              style={{ left: `${menu.x}px`, top: `${menu.y}px` }}
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-12-regular text-v2-text-text-base hover:bg-v2-background-bg-layer-02"
                onClick={() => changeStructure(menu.axis, menu.index + 1, 1)}
              >
                <IconV2 name="plus" size="small" />
                {language.t("cowork.enterprise.knowledge.add")}
              </button>
              <button
                type="button"
                class="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-12-regular text-v2-state-fg-danger hover:bg-v2-background-bg-layer-02"
                onClick={() => changeStructure(menu.axis, menu.index, -1)}
              >
                <IconV2 name="trash" size="small" />
                {language.t("common.delete")}
              </button>
            </div>
          )}
        </Show>
        <footer class="flex shrink-0 gap-1 overflow-x-auto border-t border-[#3a3a3a] bg-[#191919] px-3 py-2">
          <For each={workbook!.SheetNames}>
            {(name) => (
              <button
                type="button"
                class="shrink-0 rounded-t border px-3 py-1 text-11-medium"
                classList={{
                  "border-[#217346] bg-[#217346] text-white": state.sheet === name,
                  "border-[#3a3a3a] bg-[#2a2a2a] text-[#d8d8d8] hover:bg-[#333]": state.sheet !== name,
                }}
                onClick={() => selectSheet(name)}
              >
                {name}
              </button>
            )}
          </For>
        </footer>
      </Show>
    </main>
  )
}

function workbookRange(XLSX: XlsxModule, sheet: XlsxSheet): XlsxRange {
  return sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]) : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } }
}

function cellText(XLSX: XlsxModule, sheet: XlsxSheet, row: number, column: number, revision: number) {
  void revision
  const cell = xlsxCell(sheet[XLSX.utils.encode_cell({ r: row, c: column })])
  if (!cell) return ""
  if (cell.f) return `=${cell.f}`
  return cell.w ?? String(cell.v ?? "")
}

function parseCell(value: string, style: unknown): XlsxCell {
  if (value.startsWith("=")) return { f: value.slice(1), s: style }
  if (/^-?(?:\d+\.?\d*|\.\d+)$/.test(value.trim())) return { v: Number(value), t: "n", s: style }
  if (/^(true|false)$/i.test(value.trim())) return { v: value.trim().toLowerCase() === "true", t: "b", s: style }
  return { v: value, t: "s", s: style }
}

function xlsxCell(value: unknown): XlsxCell | undefined {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as XlsxCell) : undefined
}

function dimensionSizes(dimensions: XlsxDimension[] | undefined, count: number, axis: "row" | "column") {
  return Object.fromEntries(
    Array.from({ length: count }, (_, index) => {
      const dimension = dimensions?.[index]
      const size =
        axis === "column"
          ? (dimension?.wpx ?? (dimension?.wch ? dimension.wch * 7 + 5 : DEFAULT_COLUMN_WIDTH))
          : (dimension?.hpx ?? (dimension?.hpt ? (dimension.hpt * 96) / 72 : DEFAULT_ROW_HEIGHT))
      return [index, size]
    }),
  )
}

function columnWidth(widths: Record<number, number>, index: number) {
  return widths[index] ?? DEFAULT_COLUMN_WIDTH
}

function rowHeight(heights: Record<number, number>, index: number) {
  return heights[index] ?? DEFAULT_ROW_HEIGHT
}

function shiftDimensionSizes(sizes: Record<number, number>, index: number, offset: 1 | -1, defaultSize: number) {
  const result: Record<number, number> = {}
  for (const [key, size] of Object.entries(sizes)) {
    const current = Number(key)
    if (offset < 0 && current === index) continue
    result[current >= index ? current + offset : current] = size
  }
  if (offset > 0) result[index] = defaultSize
  return result
}

function shiftWorksheet(
  XLSX: XlsxModule,
  workbook: XlsxWorkbook,
  sheetName: string,
  axis: "row" | "column",
  index: number,
  offset: 1 | -1,
) {
  const target = workbook.Sheets[sheetName]
  if (!target) return
  const cells = Object.entries(target).filter(([address]) => /^[A-Z]+\d+$/.test(address))
  for (const [address] of cells) delete target[address]
  for (const [address, cell] of cells) {
    const coordinate = XLSX.utils.decode_cell(address)
    const position = axis === "row" ? coordinate.r : coordinate.c
    if (offset < 0 && position === index) continue
    if (position >= index) {
      if (axis === "row") coordinate.r += offset
      else coordinate.c += offset
    }
    if (coordinate.r >= 0 && coordinate.c >= 0) target[XLSX.utils.encode_cell(coordinate)] = cell
  }

  const dimensionsKey = axis === "row" ? "!rows" : "!cols"
  const dimensions = [...((target[dimensionsKey] as XlsxDimension[] | undefined) ?? [])]
  if (offset > 0)
    dimensions.splice(index, 0, axis === "row" ? { hpx: DEFAULT_ROW_HEIGHT } : { wpx: DEFAULT_COLUMN_WIDTH })
  else dimensions.splice(index, 1)
  target[dimensionsKey] = dimensions
  target["!merges"] = (target["!merges"] ?? [])
    .map((range) => shiftRange(range, axis, index, offset))
    .filter((range): range is XlsxRange => Boolean(range))
  if (target["!autofilter"]?.ref) {
    target["!autofilter"].ref = shiftFormulaReferences(
      XLSX,
      target["!autofilter"].ref,
      sheetName,
      sheetName,
      axis,
      index,
      offset,
    )
  }
  target["!ref"] = usedRange(XLSX, target)

  for (const formulaSheetName of workbook.SheetNames) {
    const formulaSheet = workbook.Sheets[formulaSheetName]
    for (const [address, value] of Object.entries(formulaSheet)) {
      if (!/^[A-Z]+\d+$/.test(address)) continue
      const cell = xlsxCell(value)
      if (!cell?.f) continue
      cell.f = shiftFormulaReferences(XLSX, cell.f, formulaSheetName, sheetName, axis, index, offset)
      delete cell.w
      delete cell.v
    }
  }
}

function shiftRange(range: XlsxRange, axis: "row" | "column", index: number, offset: 1 | -1) {
  const next: XlsxRange = { s: { ...range.s }, e: { ...range.e } }
  const start = axis === "row" ? next.s.r : next.s.c
  const end = axis === "row" ? next.e.r : next.e.c
  let shiftedStart = start
  let shiftedEnd = end
  if (offset > 0) {
    if (index <= start) {
      shiftedStart += 1
      shiftedEnd += 1
    } else if (index <= end) {
      shiftedEnd += 1
    }
  } else if (index < start) {
    shiftedStart -= 1
    shiftedEnd -= 1
  } else if (index <= end) {
    shiftedEnd -= 1
  }
  if (shiftedEnd < shiftedStart) return undefined
  if (axis === "row") {
    next.s.r = shiftedStart
    next.e.r = shiftedEnd
  } else {
    next.s.c = shiftedStart
    next.e.c = shiftedEnd
  }
  return next
}

function usedRange(XLSX: XlsxModule, sheet: XlsxSheet) {
  const coordinates = Object.keys(sheet)
    .filter((address) => /^[A-Z]+\d+$/.test(address))
    .map((address) => XLSX.utils.decode_cell(address))
  if (!coordinates.length) return "A1"
  return XLSX.utils.encode_range({
    s: {
      r: Math.min(...coordinates.map((coordinate) => coordinate.r)),
      c: Math.min(...coordinates.map((coordinate) => coordinate.c)),
    },
    e: {
      r: Math.max(...coordinates.map((coordinate) => coordinate.r)),
      c: Math.max(...coordinates.map((coordinate) => coordinate.c)),
    },
  })
}

function shiftFormulaReferences(
  XLSX: XlsxModule,
  formula: string,
  formulaSheetName: string,
  targetSheetName: string,
  axis: "row" | "column",
  index: number,
  offset: 1 | -1,
) {
  const reference = /(^|[^A-Z0-9_.])(?:(?:'((?:[^']|'')+)'|([A-Z_][A-Z0-9_.]*))!)?(\$?)([A-Z]{1,3})(\$?)(\d+)/gi
  return formula.replace(
    reference,
    (match, prefix, quotedSheet, plainSheet, columnAbsolute, column, rowAbsolute, row) => {
      const referencedSheet = quotedSheet?.replace(/''/g, "'") ?? plainSheet
      if (
        referencedSheet
          ? referencedSheet.toLowerCase() !== targetSheetName.toLowerCase()
          : formulaSheetName !== targetSheetName
      )
        return match
      const coordinate = XLSX.utils.decode_cell(`${column}${row}`)
      const position = axis === "row" ? coordinate.r : coordinate.c
      if (offset < 0 && position === index) return `${prefix}#REF!`
      if (position < index) return match
      if (axis === "row") coordinate.r += offset
      else coordinate.c += offset
      const shiftedColumn = spreadsheetColumnName(coordinate.c)
      const sheetPrefix = quotedSheet ? `'${quotedSheet}'!` : plainSheet ? `${plainSheet}!` : ""
      return `${prefix}${sheetPrefix}${columnAbsolute}${shiftedColumn}${rowAbsolute}${coordinate.r + 1}`
    },
  )
}

function normalizedFormat(format: string) {
  return format.toLowerCase().replace(/^\./, "")
}

function spreadsheetColumnName(index: number): string {
  const next = Math.floor(index / 26) - 1
  const letter = String.fromCharCode(65 + (index % 26))
  return next >= 0 ? `${spreadsheetColumnName(next)}${letter}` : letter
}

function loadXlsxModule() {
  const url = "https://esm.sh/xlsx@0.18.5"
  return import(/* @vite-ignore */ url) as Promise<XlsxModule>
}
